import { ConfirmationResult } from "firebase/auth";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { getFirebaseIdToken, resendOTPWithToken, sendOTPWithToken, signOutFirebase, verifyOTP } from "~/lib/firebase/firebaseAuthService";
import { setError } from "~/lib/store/slice/loginSlice";
import { useAppDispatch } from "~/lib/store/store";

// Global storage for confirmation result (shared across hook instances)
let globalConfirmationResult: ConfirmationResult | null = null;

interface UseFirebaseAuthReturn {
  sendFirebaseOTP: (phoneNumber: string, recaptchaToken: string) => Promise<boolean>;
  verifyFirebaseOTP: (otp: string) => Promise<string | null>;
  firebaseLoading: boolean;
  firebaseError: string | null;
  clearFirebaseError: () => void;
  signOutFirebase: () => Promise<void>;
  getFirebaseToken: () => Promise<string | null>;
  isFirebaseRecaptchaReady: boolean;
  resendFirebaseOTP: (phoneNumber: string, recaptchaToken: string) => Promise<boolean>;
}

/**
 * Custom hook for Firebase phone authentication
 * Uses existing reCAPTCHA v2 token instead of Firebase invisible reCAPTCHA
 */
export const useFirebaseAuth = (): UseFirebaseAuthReturn => {
  const dispatch = useAppDispatch();
  const [firebaseLoading, setFirebaseLoading] = useState(false);
  const [firebaseError, setFirebaseError] = useState<string | null>(null);
  // Always ready since we use existing reCAPTCHA v2 instead of Firebase invisible reCAPTCHA
  const isFirebaseRecaptchaReady = true;

  /**
   * Send OTP to phone number via Firebase using existing reCAPTCHA token
   */
  const sendFirebaseOTP = useCallback(async (phoneNumber: string, recaptchaToken: string): Promise<boolean> => {
    if (!recaptchaToken) {
      const errorMsg = "Please verify that you are not a robot";
      setFirebaseError(errorMsg);
      dispatch(setError(errorMsg));
      toast.error(errorMsg);
      return false;
    }

    setFirebaseLoading(true);
    setFirebaseError(null);

    try {
      const confirmationResult = await sendOTPWithToken(phoneNumber, recaptchaToken);

      if (confirmationResult) {
        globalConfirmationResult = confirmationResult; // Store globally
        toast.success("OTP sent successfully");
        return true;
      }

      const errorMsg = "Failed to send OTP. Please try again.";
      setFirebaseError(errorMsg);
      toast.error(errorMsg);
      dispatch(setError(errorMsg));
      return false;
    } catch (error) {
      console.error("Error in sendFirebaseOTP:", error);

      // Get error message
      let errorMessage = "Failed to send OTP";
      if (error instanceof Error) {
        errorMessage = error.message;

        // Check for specific Firebase error
        const firebaseError = error as { code?: string };
        if (firebaseError.code === "auth/internal-error") {
          errorMessage =
            "Firebase Phone Authentication is not enabled. Please enable it in Firebase Console (Authentication → Sign-in method → Phone)";
        }
      }

      setFirebaseError(errorMessage);
      toast.error(errorMessage);

      return false;
    } finally {
      setFirebaseLoading(false);
    }
  }, []);

  /**
   * Verify OTP code with Firebase
   * Returns Firebase ID token on success
   */
  const verifyFirebaseOTP = useCallback(async (otp: string): Promise<string | null> => {
    console.log("otp: ", otp);
    console.log("globalConfirmationResult:", globalConfirmationResult); // Debug log

    if (!globalConfirmationResult) {
      const errorMsg = "No confirmation result found. Please request OTP first.";
      setFirebaseError(errorMsg);
      toast.error(errorMsg);
      dispatch(setError(errorMsg));
      return null;
    }

    setFirebaseLoading(true);
    setFirebaseError(null);

    try {
      const result = await verifyOTP(globalConfirmationResult, otp);
      if (result && result.user) {
        const token = await result.user.getIdToken();
        globalConfirmationResult = null;
        return token;
      }
      return null;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to verify OTP";
      setFirebaseError(errorMessage);
      dispatch(setError(errorMessage));
      toast.error(errorMessage);
      return null;
    } finally {
      setFirebaseLoading(false);
    }
  }, []);

  /**
   * Get current Firebase ID token
   */
  const getFirebaseToken = useCallback(async (): Promise<string | null> => {
    try {
      const token = await getFirebaseIdToken();
      return token;
    } catch (error) {
      console.error("Error getting Firebase token:", error);
      return null;
    }
  }, []);

  /**
   * Resend OTP to phone number via Firebase using existing reCAPTCHA token
   */
  const resendFirebaseOTP = useCallback(
    async (phoneNumber: string, recaptchaToken: string): Promise<boolean> => {
      if (!recaptchaToken) {
        const errorMsg = "Please verify that you are not a robot";
        setFirebaseError(errorMsg);
        dispatch(setError(errorMsg));
        toast.error(errorMsg);
        return false;
      }

      setFirebaseLoading(true);
      setFirebaseError(null);

      try {
        const confirmationResult = await resendOTPWithToken(phoneNumber, recaptchaToken);

        if (confirmationResult) {
          globalConfirmationResult = confirmationResult; // Store globally
          toast.success("OTP resent successfully");
          return true;
        }

        const errorMsg = "Failed to resend OTP. Please try again.";
        setFirebaseError(errorMsg);
        toast.error(errorMsg);
        dispatch(setError(errorMsg));
        return false;
      } catch (error) {
        console.error("Error in resendFirebaseOTP:", error);

        // Get error message
        let errorMessage = "Failed to resend OTP";
        if (error instanceof Error) {
          errorMessage = error.message;

          // Check for specific Firebase error
          const firebaseError = error as { code?: string };
          if (firebaseError.code === "auth/internal-error") {
            errorMessage =
              "Firebase Phone Authentication is not enabled. Please enable it in Firebase Console (Authentication → Sign-in method → Phone)";
          }
        }

        setFirebaseError(errorMessage);
        toast.error(errorMessage);

        return false;
      } finally {
        setFirebaseLoading(false);
      }
    },
    [dispatch],
  );

  /**
   * Clear Firebase error
   */
  const clearFirebaseError = useCallback(() => {
    setFirebaseError(null);
  }, []);

  return {
    sendFirebaseOTP,
    verifyFirebaseOTP,
    firebaseLoading,
    firebaseError,
    clearFirebaseError,
    signOutFirebase,
    getFirebaseToken,
    isFirebaseRecaptchaReady,
    resendFirebaseOTP,
  };
};
